import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Block as BlockIcon,
  EventAvailable as EventAvailableIcon,
  ExpandMore as ExpandMoreIcon,
  HourglassBottom as HourglassBottomIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  LocalShipping as LocalShippingIcon,
  MoveToInbox as MoveToInboxIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  TaskAlt as TaskAltIcon,
  Whatshot as WhatshotIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import axios from '../utils/axios';
import { PageHeader, StatCard } from '../components/ui';
import DateRangeField from '../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../components/ui/ProductFilters';
import { useIsMobile } from '../utils/responsive';
import {
  MARKETPLACES,
  MP_COLORS,
  MP_TEXT_COLORS,
  PLAN_KEYS,
  PLAN_NAMES,
  PLAN_TONES,
  QUICK_RANGES,
  REFRESH_MS,
  ZONE_KEYS,
  ZONE_NAMES,
  ZONE_TONES,
  accountsFor,
  activeQuickRange,
  applyFilterChange,
  buildQuery,
  cancelledTotal,
  filterParams,
  fmtHours,
  fmtInt,
  fmtShare,
  hasPlanRating,
  hasProductFilter,
  hotView,
  isPlanOverdue,
  loadingStatusLabel,
  longDate,
  mpLabel,
  palletStatusLabel,
  parseQuery,
  phaseLabel,
  planRated,
  planShares,
  prettyError,
  printDocLabel,
  productFilterValue,
  productFiltersMp,
  quickRange,
  resolveQuery,
  shippedSourcesText,
  shortDate,
  stageLabel,
  thresholdsOf,
  warehousesFor,
  zoneOf,
  zoneRanges,
  zoneShares,
  zonesTotal,
} from './fulfillmentDashboardUi.mjs';

// ============================================================
// Хелперы
// ============================================================

function errMessage(err) {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Не удалось загрузить данные';
}

function isAbort(err) {
  return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
}

function fmtDt(iso) {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD.MM HH:mm') : String(iso);
}

function formatRange(from, to) {
  if (!from || !to) return '';
  if (from === to) return longDate(from);
  return `${longDate(from)} — ${longDate(to)}`;
}

function list(value) {
  return Array.isArray(value) ? value : [];
}

// ============================================================
// Мелкие блоки
// ============================================================

function Panel({ children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: (t) => `1px solid ${t.palette.outlineVariant}`,
        borderRadius: 3,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

function EmptyNote({ text = 'Нет данных', sx }) {
  return (
    <Typography variant="bodyMedium" color="text.secondary" sx={{ display: 'block', py: 2.5, px: 2, textAlign: 'center', ...sx }}>
      {text}
    </Typography>
  );
}

function SectionTitle({ title, caption, icon, actions, sx }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1.5, ...sx }}>
      {icon}
      <Typography component="h2" variant="titleMedium" fontWeight={600}>{title}</Typography>
      {caption && <Typography variant="bodySmall" color="text.secondary">{caption}</Typography>}
      {actions && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>}
    </Box>
  );
}

function PanelHeader({ title, count, caption }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        borderBottom: (t) => `1px solid ${t.palette.outlineVariant}`,
      }}
    >
      <Typography variant="titleSmall">{title}</Typography>
      {count != null && <Chip size="small" label={fmtInt(count)} />}
      {caption && <Typography variant="bodySmall" color="text.secondary">{caption}</Typography>}
    </Box>
  );
}

function MpBadge({ mp }) {
  if (!mp) return <Typography variant="bodySmall" color="text.secondary">—</Typography>;
  return (
    <Chip
      size="small"
      label={mp === 'yandex' ? 'Яндекс' : mpLabel(mp)}
      sx={{
        bgcolor: MP_COLORS[mp] || 'action.selected',
        color: MP_TEXT_COLORS[mp] || 'text.primary',
        fontWeight: 600,
        height: 22,
      }}
    />
  );
}

function MpDot({ mp, size = 10 }) {
  return <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: MP_COLORS[mp] || 'text.disabled', flex: '0 0 auto' }} />;
}

function Mono({ children }) {
  return (
    <Typography component="span" variant="bodySmall" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
      {children ?? '—'}
    </Typography>
  );
}

function Muted({ children }) {
  return <Typography component="span" variant="bodySmall" color="text.secondary">{children}</Typography>;
}

/** Горизонтальная полоса долей зон (норма/средне/плохо). */
function ZoneBar({ zones, thresholds, width = 120, showText = true }) {
  const theme = useTheme();
  const total = zonesTotal(zones);
  const shares = zoneShares(zones);
  const ranges = zoneRanges(thresholds);
  if (!total) return <Muted>—</Muted>;
  const tip = ZONE_KEYS.map((key) => `${ZONE_NAMES[key]} ${ranges[key]}: ${fmtInt(zones?.[key])} (${fmtShare(shares[key])})`).join(' · ');
  return (
    <Tooltip title={tip}>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25, minWidth: width }}>
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', width, bgcolor: 'action.hover' }}>
          {ZONE_KEYS.map((key) => (
            <Box key={key} sx={{ width: `${(shares[key] || 0) * 100}%`, bgcolor: theme.palette[ZONE_TONES[key]].main }} />
          ))}
        </Box>
        {showText && (
          <Typography variant="labelSmall" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 400 }}>
            {ZONE_KEYS.map((key) => fmtShare(shares[key])).join(' / ')}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/** Полоса долей отгрузок Яндекса: в плановую дату / раньше / позже. */
function PlanBar({ plan, width = 120, showText = true }) {
  const theme = useTheme();
  const rated = planRated(plan);
  const shares = planShares(plan);
  if (!rated) return <Muted>—</Muted>;
  const tip = PLAN_KEYS.map((key) => `${PLAN_NAMES[key]}: ${fmtInt(plan?.[key])} (${fmtShare(shares[key])})`).join(' · ');
  return (
    <Tooltip title={tip}>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25, minWidth: width }}>
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', width, bgcolor: 'action.hover' }}>
          {PLAN_KEYS.map((key) => (
            <Box key={key} sx={{ width: `${(shares[key] || 0) * 100}%`, bgcolor: theme.palette[PLAN_TONES[key]].main }} />
          ))}
        </Box>
        {showText && (
          <Typography variant="labelSmall" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 400 }}>
            {`в план. дату ${fmtShare(shares.on_time)} · позже ${fmtShare(shares.late)} · раньше ${fmtShare(shares.early)}`}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function HoursPair({ stat, thresholds, colorMedian }) {
  const theme = useTheme();
  if (!stat || !stat.n) return <Muted>—</Muted>;
  const zone = colorMedian ? zoneOf(stat.median, thresholds) : null;
  return (
    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
      <Box component="span" sx={{ fontWeight: 600, color: zone ? theme.palette[ZONE_TONES[zone]].dark : undefined }}>
        {fmtHours(stat.median, { unit: false })}
      </Box>
      <Muted>{` / ${fmtHours(stat.p90, { unit: false })}`}</Muted>
    </Box>
  );
}

/**
 * Компактная таблица: на узких экранах — горизонтальный скролл.
 * columns: [{ key, label, align, render(row), nowrap, minWidth, maxWidth }]
 */
function SimpleTable({ columns, rows, rowKey, maxHeight, empty = 'Нет данных', footer }) {
  const items = list(rows);
  if (!items.length && !footer) return <EmptyNote text={empty} />;
  return (
    <TableContainer sx={{ maxHeight, overflowX: 'auto' }}>
      <Table size="small" stickyHeader={Boolean(maxHeight)}>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                align={col.align}
                sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: 'text.secondary', bgcolor: 'background.paper', minWidth: col.minWidth }}
              >
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row, idx) => (
            <TableRow key={rowKey ? rowKey(row, idx) : idx} hover>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={{
                    whiteSpace: col.nowrap ? 'nowrap' : undefined,
                    maxWidth: col.maxWidth,
                    overflowWrap: col.maxWidth ? 'anywhere' : undefined,
                  }}
                >
                  {col.render ? col.render(row) : (row[col.key] ?? '—')}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {footer}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** Счётчик с раскрытием списка. */
function CountAccordion({ title, count, tone = 'error', caption, children }) {
  const active = Number(count) > 0;
  return (
    <Accordion
      disableGutters
      elevation={0}
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        border: (t) => `1px solid ${t.palette.outlineVariant}`,
        borderRadius: '12px !important',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        '&:before': { display: 'none' },
        mb: 1.5,
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={<ExpandMoreIcon />}
        sx={{
          flexDirection: 'row-reverse',
          px: 1.5,
          '& .MuiAccordionSummary-content': { my: 1, ml: 0.75, alignItems: 'center', gap: 1.25, flexWrap: 'wrap', minWidth: 0 },
        }}
      >
        <Chip
          label={fmtInt(count)}
          color={active ? tone : 'default'}
          variant={active ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700, minWidth: 44 }}
        />
        <Typography variant="titleSmall">{title}</Typography>
        {caption && <Typography variant="bodySmall" color="text.secondary">{caption}</Typography>}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0, borderTop: (t) => `1px solid ${t.palette.outlineVariant}` }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// ============================================================
// Фильтры
// ============================================================

function FiltersBar({ meta, query, onChange, isMobile }) {
  const mps = list(meta?.marketplaces).length ? meta.marketplaces : MARKETPLACES;
  const accounts = accountsFor(meta?.accounts, query.mp);
  const warehouses = warehousesFor(meta?.warehouses, query.mp, query.account_id);
  const selectedWh = list(meta?.warehouses).find((w) => w.key === query.warehouse) || null;
  const whOptions = selectedWh && !warehouses.some((w) => w.key === selectedWh.key) ? [selectedWh, ...warehouses] : warehouses;
  const productActive = hasProductFilter(query.product);
  const anyFilter = Boolean(query.mp || query.account_id || query.warehouse || productActive);
  const productValue = { ...productFilterValue(query.product), mp: productFiltersMp(query.mp) };

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={1.5}
      alignItems={isMobile ? 'stretch' : 'center'}
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 2 }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={query.mp || 'all'}
        fullWidth={isMobile}
        onChange={(_, value) => {
          if (value == null) return;
          onChange({ mp: value === 'all' ? '' : value });
        }}
      >
        <ToggleButton value="all" sx={{ px: 1.5, textTransform: 'none' }}>Все</ToggleButton>
        {mps.map((mp) => (
          <ToggleButton key={mp} value={mp} sx={{ px: 1.5, textTransform: 'none', whiteSpace: 'nowrap', gap: 0.75 }}>
            <MpDot mp={mp} size={8} />
            {mp === 'yandex' ? 'Яндекс' : mpLabel(mp)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TextField
        select
        size="small"
        label="Кабинет"
        value={query.account_id ?? ''}
        onChange={(e) => onChange({ account_id: e.target.value === '' ? null : Number(e.target.value) })}
        sx={{ minWidth: isMobile ? 0 : 220 }}
        fullWidth={isMobile}
        disabled={!meta}
      >
        <MenuItem value="">Все кабинеты</MenuItem>
        {accounts.map((a) => (
          <MenuItem key={a.id} value={a.id} sx={{ gap: 1 }}>
            <MpDot mp={a.marketplace} size={8} />
            <Box component="span" sx={{ color: a.active ? 'text.primary' : 'text.secondary' }}>
              {a.name}{a.active ? '' : ' (неактивен)'}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        size="small"
        options={whOptions}
        value={selectedWh}
        onChange={(_, value) => onChange({ warehouse: value?.key || '' })}
        isOptionEqualToValue={(opt, value) => opt.key === value.key}
        getOptionLabel={(opt) => opt?.name || opt?.key || ''}
        noOptionsText="Нет складов"
        disabled={!meta}
        renderOption={(props, opt) => {
          const { key, ...rest } = props;
          return (
            <Box component="li" key={key} {...rest} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <MpDot mp={opt.marketplace} size={8} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="bodyMedium" sx={{ overflowWrap: 'anywhere' }}>{opt.name || opt.key}</Typography>
                <Typography variant="bodySmall" color="text.secondary">
                  {mpLabel(opt.marketplace)} · {fmtInt(opt.orders_30d)} заказов за 30 дн.
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => <TextField {...params} label="Склад" placeholder="Все склады" />}
        sx={{ width: isMobile ? '100%' : 260 }}
      />

      <Box sx={{ flex: isMobile ? 'none' : '1 1 560px', minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
        <ProductFilters
          value={productValue}
          onChange={(value) => onChange({ product: buildFilterParams({ ...value, mp: '' }) })}
          showMp={false}
          showOrganization={false}
          showExcludeMode
          filterMd={4}
        />
      </Box>

      {anyFilter && (
        <Button
          size="small"
          onClick={() => onChange({ mp: '', account_id: null, warehouse: '', product: {} })}
          sx={{ textTransform: 'none', alignSelf: isMobile ? 'flex-start' : 'center' }}
        >
          Сбросить фильтры
        </Button>
      )}

      {productActive && (
        <Typography variant="bodySmall" color="text.secondary" sx={{ flexBasis: '100%', px: 0.5 }}>
          Фильтр по товару: заказ учитывается, если в нём есть хотя бы один подходящий товар (заказы без карточки
          товара не попадают); подборы, палеты и погрузки — если в них есть такой заказ. Сбои печати и счётчики
          заказов в списке складов фильтр по товару не учитывают.
        </Typography>
      )}
    </Stack>
  );
}

// ============================================================
// Вкладка «Сейчас»
// ============================================================

const TILE_METRICS = [
  { key: 'queue', label: 'В очереди' },
  { key: 'assembly', label: 'В сборке' },
  { key: 'picked', label: 'Собрано, ждёт паллеты' },
  { key: 'on_pallet', label: 'На паллете' },
  { key: 'shipped_today', label: 'Отгружено сегодня', tone: 'success' },
];

function MpTileCard({ tile }) {
  const theme = useTheme();
  const mp = tile.marketplace;
  return (
    <Panel sx={{ height: '100%', borderTop: `4px solid ${MP_COLORS[mp] || theme.palette.primary.main}` }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
          <MpDot mp={mp} />
          <Typography variant="titleMedium" fontWeight={600}>{mpLabel(mp)}</Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {tile.rating === 'plan' ? (
              <>
                {Number(tile.stuck) > 0 && <Chip size="small" color="error" label={`Просрочено ${fmtInt(tile.stuck)}`} />}
                {Number(tile.due_today) > 0 && <Chip size="small" color="warning" label={`На сегодня ${fmtInt(tile.due_today)}`} />}
              </>
            ) : (
              <>
                {Number(tile.stuck) > 0 && <Chip size="small" color="error" label={`Зависших ${fmtInt(tile.stuck)}`} />}
                {Number(tile.near_stuck) > 0 && <Chip size="small" color="warning" label={`Близко ${fmtInt(tile.near_stuck)}`} />}
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1.25 }}>
          {TILE_METRICS.map((m) => (
            <Box
              key={m.key}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: m.tone ? alpha(theme.palette[m.tone].main, 0.1) : 'action.hover',
              }}
            >
              <Typography variant="bodySmall" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, minHeight: '2.6em' }}>
                {m.label}
              </Typography>
              <Typography variant="headlineSmall" fontWeight={600} color={m.tone ? `${m.tone}.dark` : 'text.primary'}>
                {fmtInt(tile[m.key])}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="bodySmall" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
          Поступило сегодня {fmtInt(tile.received_today)} · Отменено сегодня {fmtInt(tile.cancelled_today)}
        </Typography>
      </Box>
    </Panel>
  );
}

function AgeCell({ hours, thresholds, rated = true }) {
  const t = thresholdsOf(thresholds);
  const h = Number(hours);
  let color;
  if (!rated) color = undefined;
  else if (h >= t.stuck_hours) color = 'error.main';
  else if (h >= t.near_stuck_hours) color = 'warning.dark';
  return (
    <Typography component="span" variant="bodySmall" fontWeight={color ? 700 : 400} color={color} sx={{ whiteSpace: 'nowrap' }}>
      {fmtHours(hours)}
    </Typography>
  );
}

function MpStatusCell({ row }) {
  if (!row.mp_status && !row.mp_substatus) return <Muted>—</Muted>;
  return (
    <Box sx={{ lineHeight: 1.2 }}>
      <Typography variant="bodySmall" sx={{ display: 'block' }}>{row.mp_status || '—'}</Typography>
      {row.mp_substatus && <Typography variant="labelSmall" color="text.secondary" sx={{ fontWeight: 400 }}>{row.mp_substatus}</Typography>}
    </Box>
  );
}

function hotColumns(thresholds, today) {
  return [
    { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
    { key: 'ext_id', label: 'Заказ', render: (r) => <Mono>{r.ext_id}</Mono> },
    { key: 'account', label: 'Кабинет', render: (r) => r.account_name || '—', nowrap: true },
    { key: 'article', label: 'Артикул', render: (r) => r.article || '—', maxWidth: 240 },
    { key: 'created', label: 'Создан', render: (r) => fmtDt(r.created_at), nowrap: true },
    { key: 'age', label: 'Возраст', align: 'right', render: (r) => <AgeCell hours={r.age_hours} thresholds={thresholds} rated={r.rating !== 'plan'} /> },
    { key: 'stage', label: 'Этап', render: (r) => stageLabel(r.stage), nowrap: true },
    { key: 'mp_status', label: 'Статус МП', render: (r) => <MpStatusCell row={r} /> },
    {
      key: 'planned',
      label: 'Плановая отгрузка',
      nowrap: true,
      render: (r) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <span>{r.planned_shipment_day ? shortDate(r.planned_shipment_day) : '—'}</span>
          {isPlanOverdue(r, today) && <Chip size="small" variant="outlined" color="error" label="просрочено" sx={{ height: 20 }} />}
        </Box>
      ),
    },
  ];
}

function HotSection({ now, thresholds }) {
  const view = hotView(now);
  const t = thresholdsOf(thresholds);
  const tiles = list(now?.tiles);
  const withPlan = hasPlanRating(tiles);
  const withHours = tiles.some((tile) => tile.rating !== 'plan');
  const columns = hotColumns(t, now?.today);
  const truncNote = (shown, total) => (shown < total ? `показаны первые ${fmtInt(shown)} из ${fmtInt(total)}` : null);
  const rules = [
    withHours ? `WB и Ozon — старше ${fmtInt(t.stuck_hours)} ч от создания` : null,
    withPlan ? 'Яндекс — плановая дата отгрузки прошла' : null,
  ].filter(Boolean).join('; ');
  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle title="Горит" icon={<WhatshotIcon color="error" />} caption={`зависшие: ${rules}`} />
      <CountAccordion
        title="Зависшие"
        count={view.stuckCount}
        tone="error"
        caption={truncNote(view.stuck.length, view.stuckCount)}
      >
        <SimpleTable columns={columns} rows={view.stuck} rowKey={(r) => `${r.marketplace}:${r.id ?? r.ext_id}`} maxHeight={520} empty="Всё в порядке" />
      </CountAccordion>
      {withHours && (
        <CountAccordion
          title={`Приближаются: WB и Ozon ${fmtInt(t.near_stuck_hours)}–${fmtInt(t.stuck_hours)} ч`}
          count={view.nearCount}
          tone="warning"
          caption={truncNote(view.near.length, view.nearCount)}
        >
          <SimpleTable columns={columns} rows={view.near} rowKey={(r) => `${r.marketplace}:${r.id ?? r.ext_id}`} maxHeight={520} empty="Всё в порядке" />
        </CountAccordion>
      )}
      {withPlan && (
        <CountAccordion
          title="Яндекс: плановая отгрузка сегодня"
          count={view.dueTodayCount}
          tone="warning"
          caption={truncNote(view.dueToday.length, view.dueTodayCount)}
        >
          <SimpleTable columns={columns} rows={view.dueToday} rowKey={(r) => `${r.marketplace}:${r.id ?? r.ext_id}`} maxHeight={520} empty="Нет неотгруженных на сегодня" />
        </CountAccordion>
      )}
    </Box>
  );
}

function PickedProgress({ picked, total }) {
  const p = Number(picked) || 0;
  const n = Number(total) || 0;
  return (
    <Box sx={{ minWidth: 90 }}>
      <Typography variant="bodySmall" sx={{ whiteSpace: 'nowrap' }}>{fmtInt(p)} из {fmtInt(n)}</Typography>
      <LinearProgress variant="determinate" value={n ? Math.min(100, (p / n) * 100) : 0} sx={{ height: 4, borderRadius: 2, mt: 0.25 }} />
    </Box>
  );
}

const PICKING_COLUMNS = [
  { key: 'code', label: 'Код', render: (r) => <Mono>{r.code}</Mono> },
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  {
    key: 'phase',
    label: 'Фаза',
    render: (r) => <Chip size="small" variant="outlined" color={r.phase === 'labeling' ? 'secondary' : 'primary'} label={phaseLabel(r.phase)} />,
  },
  { key: 'assignee', label: 'Исполнитель', render: (r) => r.assignee || '—', nowrap: true },
  { key: 'labeler', label: 'Маркировщик', render: (r) => r.labeler || '—', nowrap: true },
  { key: 'picked', label: 'Собрано', render: (r) => <PickedProgress picked={r.items_picked} total={r.items_total} /> },
  { key: 'age', label: 'В работе', align: 'right', render: (r) => fmtHours(r.age_hours), nowrap: true },
];

const PALLET_TONES = { loading: 'info', closed: 'success' };

const PALLET_COLUMNS = [
  { key: 'code', label: 'Код', render: (r) => <Mono>{r.code}</Mono> },
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  {
    key: 'status',
    label: 'Статус',
    render: (r) => <Chip size="small" variant="outlined" color={PALLET_TONES[r.status] || 'default'} label={palletStatusLabel(r.status)} />,
  },
  { key: 'items', label: 'Заказов', align: 'right', render: (r) => fmtInt(r.items_count) },
  { key: 'created_by', label: 'Создал', render: (r) => r.created_by || '—', nowrap: true },
  { key: 'closed_by', label: 'Закрыл', render: (r) => r.closed_by || '—', nowrap: true },
  { key: 'age', label: 'Возраст', align: 'right', render: (r) => fmtHours(r.age_hours), nowrap: true },
];

const LOADING_TONES = { failed: 'error', partial: 'warning', confirming: 'info' };

function ErrorText({ text }) {
  const s = prettyError(text);
  return s ? <Typography variant="bodySmall" color="error.main">{s}</Typography> : <Muted>—</Muted>;
}

const LOADING_COLUMNS = [
  { key: 'code', label: 'Код', render: (r) => <Mono>{r.code}</Mono> },
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  {
    key: 'status',
    label: 'Статус',
    render: (r) => <Chip size="small" variant="outlined" color={LOADING_TONES[r.status] || 'default'} label={loadingStatusLabel(r.status)} />,
  },
  { key: 'pallets', label: 'Паллет', align: 'right', render: (r) => fmtInt(r.pallets_count) },
  { key: 'created_by', label: 'Создал', render: (r) => r.created_by || '—', nowrap: true },
  { key: 'age', label: 'Возраст', align: 'right', render: (r) => fmtHours(r.age_hours), nowrap: true },
  { key: 'error', label: 'Ошибка', minWidth: 200, maxWidth: 420, render: (r) => <ErrorText text={r.error_message} /> },
];

const MS_ERROR_COLUMNS = [
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  { key: 'ext_id', label: 'Заказ', render: (r) => <Mono>{r.ext_id}</Mono> },
  { key: 'account', label: 'Кабинет', render: (r) => r.account_name || '—', nowrap: true },
  { key: 'article', label: 'Артикул', render: (r) => r.article || '—', maxWidth: 220 },
  { key: 'error', label: 'Ошибка', minWidth: 220, maxWidth: 420, render: (r) => <ErrorText text={r.error} /> },
  { key: 'at', label: 'Когда', render: (r) => fmtDt(r.error_at), nowrap: true },
];

const LOADING_ERROR_COLUMNS = [
  { key: 'at', label: 'Когда', render: (r) => fmtDt(r.at), nowrap: true },
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  { key: 'session', label: 'Погрузка', render: (r) => <Mono>{r.session_code}</Mono> },
  {
    key: 'status',
    label: 'Статус',
    render: (r) => {
      const status = r.session_status || r.event_type;
      return status ? <Chip size="small" variant="outlined" color={LOADING_TONES[status] || 'default'} label={loadingStatusLabel(status)} /> : <Muted>—</Muted>;
    },
  },
  { key: 'ext_id', label: 'Заказ', render: (r) => (r.ext_id ? <Mono>{r.ext_id}</Mono> : <Muted>—</Muted>) },
  { key: 'error', label: 'Ошибка', minWidth: 220, maxWidth: 480, render: (r) => <ErrorText text={r.error} /> },
];

const COMPOSITION_COLUMNS = [
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  { key: 'ext_id', label: 'Заказ', render: (r) => <Mono>{r.ext_id}</Mono> },
  { key: 'account', label: 'Кабинет', render: (r) => r.account_name || '—', nowrap: true },
  { key: 'article', label: 'Артикул', render: (r) => r.article || '—', maxWidth: 240 },
  { key: 'stage', label: 'Этап', render: (r) => stageLabel(r.stage), nowrap: true },
  { key: 'at', label: 'Когда', render: (r) => fmtDt(r.alert_at), nowrap: true },
];

const orderKey = (r) => `${r.marketplace}:${r.id ?? r.ext_id}`;

function WorkPanel({ title, rows, columns, empty }) {
  return (
    <Panel sx={{ height: '100%' }}>
      <PanelHeader title={title} count={list(rows).length} />
      <SimpleTable columns={columns} rows={rows} rowKey={(r) => `${r.marketplace}:${r.id}`} maxHeight={420} empty={empty} />
    </Panel>
  );
}

function NowTab({ now, loading, error, onRefresh, thresholds }) {
  const updated = now?.generated_at ? dayjs(now.generated_at).format('HH:mm:ss') : null;
  const tiles = list(now?.tiles);
  const problems = now?.problems || {};
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="bodySmall" color="text.secondary">
          {updated ? `Обновлено ${updated}` : 'Загрузка…'} · автообновление раз в минуту
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
          sx={{ textTransform: 'none', ml: 'auto' }}
        >
          Обновить
        </Button>
      </Box>
      <Box sx={{ height: 4, mb: 1.5 }}>{loading && <LinearProgress />}</Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!now && loading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[0, 1, 2].map((i) => (
            <Grid item xs={12} md={6} lg={4} key={i}><Skeleton variant="rounded" height={230} /></Grid>
          ))}
        </Grid>
      )}

      {now && (
        <>
          {tiles.length ? (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {tiles.map((tile) => (
                <Grid item xs={12} md={6} lg={tiles.length >= 3 ? 4 : 6} key={tile.marketplace}>
                  <MpTileCard tile={tile} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Panel sx={{ mb: 3 }}><EmptyNote /></Panel>
          )}

          <HotSection now={now} thresholds={thresholds} />

          <SectionTitle title="Работа в процессе" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <WorkPanel title="Открытые подборы" rows={now.open_picking_jobs} columns={PICKING_COLUMNS} empty="Нет открытых подборов" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <WorkPanel title="Паллеты в работе" rows={now.open_pallets} columns={PALLET_COLUMNS} empty="Нет паллет в работе" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <WorkPanel title="Погрузки в работе" rows={now.open_loadings} columns={LOADING_COLUMNS} empty="Нет погрузок в работе" />
            </Grid>
          </Grid>

          <SectionTitle title="Проблемы сейчас" />
          <CountAccordion title="Ошибки отгрузки в МС" count={list(problems.ms_demand_errors).length}>
            <SimpleTable columns={MS_ERROR_COLUMNS} rows={problems.ms_demand_errors} rowKey={orderKey} maxHeight={420} empty="Всё в порядке" />
          </CountAccordion>
          <CountAccordion title="Ошибки погрузки" count={list(problems.loading_errors).length}>
            <SimpleTable columns={LOADING_ERROR_COLUMNS} rows={problems.loading_errors} maxHeight={420} empty="Всё в порядке" />
          </CountAccordion>
          <CountAccordion title="Расхождения состава" count={list(problems.composition_alerts).length} tone="warning">
            <SimpleTable columns={COMPOSITION_COLUMNS} rows={problems.composition_alerts} rowKey={orderKey} maxHeight={420} empty="Всё в порядке" />
          </CountAccordion>
        </>
      )}
    </Box>
  );
}

// ============================================================
// Вкладка «Аналитика»
// ============================================================

function PeriodToolbar({ from, to, onRange, isMobile }) {
  const active = activeQuickRange(from, to);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ width: isMobile ? '100%' : 320, maxWidth: '100%' }}>
        <DateRangeField
          label="Период"
          valueFrom={from}
          valueTo={to}
          onChange={({ from: nextFrom, to: nextTo }) => onRange(nextFrom, nextTo)}
        />
      </Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={active}
        fullWidth={isMobile}
        onChange={(_, kind) => {
          if (!kind) return;
          const range = quickRange(kind, new Date());
          onRange(range.from, range.to);
        }}
      >
        {QUICK_RANGES.map((item) => (
          <ToggleButton key={item.kind} value={item.kind} sx={{ px: 1.5, textTransform: 'none', whiteSpace: 'nowrap' }}>
            {item.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

function mpGroups(byMarketplace, planMps) {
  const plan = list(planMps);
  const mps = list(byMarketplace).map((r) => r.marketplace);
  const hours = mps.filter((mp) => !plan.includes(mp));
  return {
    plan: mps.filter((mp) => plan.includes(mp)),
    hours,
    hoursLabel: hours.map(mpLabel).join(' + '),
  };
}

function KpiRow({ total, byMarketplace, planMps, thresholds }) {
  const t = total || {};
  const ranges = zoneRanges(thresholds);
  const zonesN = zonesTotal(t.zones);
  const groups = mpGroups(byMarketplace, planMps);
  const timeSuffix = groups.plan.length && groups.hours.length ? ` (${groups.hoursLabel})` : '';
  const planRows = list(byMarketplace).filter((r) => groups.plan.includes(r.marketplace));
  const cards = [
    { key: 'received', title: 'Поступило', value: fmtInt(t.received), subtitle: 'заказов за период', icon: <MoveToInboxIcon />, color: 'info' },
    {
      key: 'shipped',
      title: 'Отгружено',
      value: fmtInt(t.shipped),
      subtitle: Number(t.shipped_no_ts) > 0 ? `из них без отметки времени ${fmtInt(t.shipped_no_ts)}` : 'заказов за период',
      icon: <LocalShippingIcon />,
      color: 'success',
    },
    {
      key: 'cancelled',
      title: 'Отменено',
      value: fmtInt(cancelledTotal(t)),
      subtitle: `до сборки ${fmtInt(t.cancelled_before)} · в сборке ${fmtInt(t.cancelled_in_assembly)} · после ${fmtInt(t.cancelled_after)}`,
      icon: <BlockIcon />,
      color: 'error',
    },
    {
      key: 'median',
      title: `Медиана до отгрузки${timeSuffix}`,
      value: fmtHours(t.time_shipped?.median),
      subtitle: t.time_shipped?.n ? `по ${fmtInt(t.time_shipped.n)} заказам` : 'нет отгрузок',
      icon: <ScheduleIcon />,
      color: 'primary',
    },
    {
      key: 'p90',
      title: `p90 до отгрузки${timeSuffix}`,
      value: fmtHours(t.time_shipped?.p90),
      subtitle: '90 % заказов отгружены быстрее',
      icon: <HourglassBottomIcon />,
      color: 'secondary',
    },
    groups.hours.length ? {
      key: 'good',
      title: `В зоне «норма» (${ranges.good})${timeSuffix}`,
      value: fmtShare(zoneShares(t.zones).good),
      subtitle: zonesN ? `${fmtInt(t.zones?.good)} из ${fmtInt(zonesN)} отгруженных` : 'нет отгрузок',
      icon: <TaskAltIcon />,
      color: 'success',
    } : null,
    ...planRows.map((row) => {
      const shares = planShares(row.plan);
      return {
        key: `plan-${row.marketplace}`,
        title: `${mpLabel(row.marketplace)}: в плановую дату`,
        value: fmtShare(shares.on_time),
        subtitle: planRated(row.plan)
          ? `позже ${fmtShare(shares.late)} · раньше ${fmtShare(shares.early)} · из ${fmtInt(planRated(row.plan))}`
          : 'нет отгрузок',
        icon: <EventAvailableIcon />,
        color: 'success',
      };
    }),
  ].filter(Boolean);
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid item xs={6} md={4} lg={cards.length > 6 ? true : 2} key={card.key}>
          <StatCard title={card.title} value={card.value} subtitle={card.subtitle} icon={card.icon} color={card.color} />
        </Grid>
      ))}
    </Grid>
  );
}

function ChartPanel({ title, caption, children }) {
  return (
    <Panel sx={{ height: '100%' }}>
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="titleSmall">{title}</Typography>
          {caption && <Typography variant="bodySmall" color="text.secondary">{caption}</Typography>}
        </Box>
        <Box sx={{ height: 240, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        </Box>
      </Box>
    </Panel>
  );
}

function AnalyticsCharts({ days, thresholds, groups }) {
  const theme = useTheme();
  const t = thresholdsOf(thresholds);
  const ranges = zoneRanges(t);
  const tick = { fontSize: 11, fill: theme.palette.text.secondary };
  const tooltip = {
    contentStyle: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      fontSize: 12,
    },
    labelStyle: { color: theme.palette.text.primary, fontWeight: 600 },
    labelFormatter: (value) => longDate(value),
  };
  const cursor = { fill: alpha(theme.palette.primary.main, 0.06) };
  const counts = days.map((d) => ({ date: d.date, received: d.received, shipped: d.shipped, cancelled: cancelledTotal(d) }));
  const times = days.map((d) => ({
    date: d.date,
    median: d.time_shipped?.n ? d.time_shipped.median : null,
    p90: d.time_shipped?.n ? d.time_shipped.p90 : null,
    handed: d.time_handed?.n ? d.time_handed.median : null,
  }));
  const zones = days.map((d) => ({ date: d.date, good: d.zones?.good || 0, mid: d.zones?.mid || 0, bad: d.zones?.bad || 0 }));
  const plans = days.map((d) => ({ date: d.date, on_time: d.plan?.on_time || 0, early: d.plan?.early || 0, late: d.plan?.late || 0 }));
  const withHours = groups.hours.length > 0;
  const withPlan = groups.plan.length > 0;
  const span = withHours && withPlan ? 6 : 4;
  const suffix = withHours && withPlan ? ` (${groups.hoursLabel})` : '';
  const xAxis = <XAxis dataKey="date" tickFormatter={shortDate} tick={tick} axisLine={false} tickLine={false} minTickGap={8} />;
  const legend = <Legend wrapperStyle={{ fontSize: 12 }} iconSize={10} />;
  const grid = <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeDasharray="3 3" />;
  const margin = { top: 8, right: 8, left: 0, bottom: 0 };
  const maxBar = 28;
  const dots = days.length <= 31;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} lg={span}>
        <ChartPanel title="Поступило / отгружено / отменено">
          <BarChart data={counts} margin={margin}>
            {grid}
            {xAxis}
            <YAxis tick={tick} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
            <ChartTooltip {...tooltip} cursor={cursor} formatter={(v, name) => [fmtInt(v), name]} />
            {legend}
            <Bar dataKey="received" name="Поступило" fill={theme.palette.info.main} radius={[3, 3, 0, 0]} maxBarSize={maxBar} isAnimationActive={false} />
            <Bar dataKey="shipped" name="Отгружено" fill={theme.palette.success.main} radius={[3, 3, 0, 0]} maxBarSize={maxBar} isAnimationActive={false} />
            <Bar dataKey="cancelled" name="Отменено" fill={theme.palette.error.main} radius={[3, 3, 0, 0]} maxBarSize={maxBar} isAnimationActive={false} />
          </BarChart>
        </ChartPanel>
      </Grid>
      <Grid item xs={12} lg={span}>
        <ChartPanel title={`Время до отгрузки, ч${suffix}`} caption="по дню отгрузки">
          <LineChart data={times} margin={margin}>
            {grid}
            {xAxis}
            <YAxis tick={tick} axisLine={false} tickLine={false} width={40} />
            <ChartTooltip {...tooltip} formatter={(v, name) => [fmtHours(v), name]} />
            {legend}
            <ReferenceLine y={t.zone_good_max_hours} stroke={theme.palette.success.main} strokeDasharray="4 4" />
            <ReferenceLine y={t.zone_mid_max_hours} stroke={theme.palette.warning.main} strokeDasharray="4 4" />
            <Line dataKey="median" name="Медиана" stroke={theme.palette.primary.main} strokeWidth={2} dot={dots} connectNulls isAnimationActive={false} />
            <Line dataKey="p90" name="p90" stroke={theme.palette.error.main} strokeWidth={2} dot={dots} connectNulls isAnimationActive={false} />
            <Line dataKey="handed" name="Медиана до передачи на сборку" stroke={theme.palette.text.secondary} strokeDasharray="5 3" dot={false} connectNulls isAnimationActive={false} />
          </LineChart>
        </ChartPanel>
      </Grid>
      {withHours && (
      <Grid item xs={12} lg={span}>
        <ChartPanel title={`Зоны по дням${suffix}`} caption="отгруженные заказы">
          <BarChart data={zones} margin={margin}>
            {grid}
            {xAxis}
            <YAxis tick={tick} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
            <ChartTooltip {...tooltip} cursor={cursor} formatter={(v, name) => [fmtInt(v), name]} />
            {legend}
            {ZONE_KEYS.map((key, idx) => (
              <Bar
                key={key}
                dataKey={key}
                name={`${ranges[key]} ${ZONE_NAMES[key]}`}
                stackId="zones"
                fill={theme.palette[ZONE_TONES[key]].main}
                radius={idx === ZONE_KEYS.length - 1 ? [3, 3, 0, 0] : undefined}
                maxBarSize={maxBar}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ChartPanel>
      </Grid>
      )}
      {withPlan && (
        <Grid item xs={12} lg={span}>
          <ChartPanel title={`${groups.plan.map(mpLabel).join(' + ')}: плановая дата`} caption="отгруженные заказы по дню отгрузки">
            <BarChart data={plans} margin={margin}>
              {grid}
              {xAxis}
              <YAxis tick={tick} axisLine={false} tickLine={false} width={40} allowDecimals={false} />
              <ChartTooltip {...tooltip} cursor={cursor} formatter={(v, name) => [fmtInt(v), name]} />
              {legend}
              {PLAN_KEYS.map((key, idx) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={PLAN_NAMES[key]}
                  stackId="plan"
                  fill={theme.palette[PLAN_TONES[key]].main}
                  radius={idx === PLAN_KEYS.length - 1 ? [3, 3, 0, 0] : undefined}
                  maxBarSize={maxBar}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ChartPanel>
        </Grid>
      )}
    </Grid>
  );
}

function CancelCell({ row }) {
  const total = cancelledTotal(row);
  return (
    <Tooltip title={`до сборки ${fmtInt(row.cancelled_before)} · в сборке ${fmtInt(row.cancelled_in_assembly)} · после сборки ${fmtInt(row.cancelled_after)}`}>
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>{fmtInt(total)}</Box>
        {total > 0 && <Muted>{` (${fmtInt(row.cancelled_before)}/${fmtInt(row.cancelled_in_assembly)}/${fmtInt(row.cancelled_after)})`}</Muted>}
      </Box>
    </Tooltip>
  );
}

function timeColumns(thresholds, groups, { splitPlan = false } = {}) {
  const isPlanRow = (r) => groups.plan.includes(r.marketplace);
  const cols = [
    { key: 'th', label: 'До передачи на сборку, мед / p90', align: 'right', render: (r) => <HoursPair stat={r.time_handed} /> },
    { key: 'tc', label: 'До сборки, мед / p90', align: 'right', render: (r) => <HoursPair stat={r.time_collected} /> },
    {
      key: 'ts',
      label: 'До отгрузки, мед / p90',
      align: 'right',
      render: (r) => <HoursPair stat={r.time_shipped} thresholds={thresholds} colorMedian={!isPlanRow(r)} />,
    },
  ];
  if (splitPlan) {
    if (groups.hours.length) {
      cols.push({ key: 'zones', label: `Зоны${groups.plan.length ? ` (${groups.hoursLabel})` : ''}`, render: (r) => <ZoneBar zones={r.zones} thresholds={thresholds} /> });
    }
    if (groups.plan.length) {
      cols.push({ key: 'plan', label: `${groups.plan.map(mpLabel).join(' + ')}: плановая дата`, render: (r) => <PlanBar plan={r.plan} /> });
    }
  } else {
    cols.push({
      key: 'zones',
      label: groups.plan.length ? 'Оценка: зоны / плановая дата' : 'Зоны',
      render: (r) => (isPlanRow(r) ? <PlanBar plan={r.plan} /> : <ZoneBar zones={r.zones} thresholds={thresholds} />),
    });
  }
  return cols;
}

function TotalRow({ columns, row }) {
  return (
    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: (t) => `2px solid ${t.palette.outlineVariant}` } }}>
      {columns.map((col, idx) => (
        <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap' }}>
          {idx === 0 ? 'Итого' : (col.render ? col.render(row) : (row[col.key] ?? '—'))}
        </TableCell>
      ))}
    </TableRow>
  );
}

function ByMarketplaceTable({ rows, total, thresholds, groups }) {
  const columns = [
    {
      key: 'mp',
      label: 'МП',
      nowrap: true,
      render: (r) => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <MpDot mp={r.marketplace} />
          {mpLabel(r.marketplace)}
        </Box>
      ),
    },
    { key: 'received', label: 'Поступило', align: 'right', render: (r) => fmtInt(r.received) },
    { key: 'shipped', label: 'Отгружено', align: 'right', render: (r) => fmtInt(r.shipped) },
    { key: 'cancelled', label: 'Отмены (до / в / после)', align: 'right', render: (r) => <CancelCell row={r} /> },
    ...timeColumns(thresholds, groups),
  ];
  const caption = groups.plan.length && groups.hours.length
    ? `время в часах от создания заказа; в «Итого» время и зоны — ${groups.hoursLabel}, ${groups.plan.map(mpLabel).join(' + ')} оценивается по плановой дате, время у него справочно`
    : 'время в часах от создания заказа';
  return (
    <Panel sx={{ mb: 3 }}>
      <PanelHeader title="По маркетплейсам" caption={caption} />
      <SimpleTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.marketplace}
        footer={list(rows).length > 1 && total ? <TotalRow columns={columns} row={total} /> : null}
      />
    </Panel>
  );
}

function ByDayTable({ days, total, thresholds, groups }) {
  const columns = [
    { key: 'date', label: 'Дата', render: (r) => shortDate(r.date), nowrap: true },
    { key: 'received', label: 'Поступило', align: 'right', render: (r) => fmtInt(r.received) },
    { key: 'shipped', label: 'Отгружено', align: 'right', render: (r) => fmtInt(r.shipped) },
    { key: 'cancelled', label: 'Отмены (до / в / после)', align: 'right', render: (r) => <CancelCell row={r} /> },
    { key: 'pallets', label: 'Паллет', align: 'right', render: (r) => fmtInt(r.pallets) },
    { key: 'loadings', label: 'Погрузок', align: 'right', render: (r) => fmtInt(r.loadings) },
    ...timeColumns(thresholds, groups, { splitPlan: true }),
  ];
  return (
    <Panel sx={{ mb: 1 }}>
      <PanelHeader title="По дням" />
      <SimpleTable
        columns={columns}
        rows={days}
        rowKey={(r) => r.date}
        maxHeight={560}
        footer={list(days).length > 1 && total ? <TotalRow columns={columns} row={total} /> : null}
      />
    </Panel>
  );
}

const ROLE_DEFS = [
  { key: 'picker', label: 'Сборщик' },
  { key: 'labeler', label: 'Маркировщик' },
  { key: 'loader', label: 'Грузчик' },
];

function EmployeeRow({ row, thresholds, withPlan }) {
  const [open, setOpen] = useState(false);
  const all = row.all || {};
  return (
    <>
      <TableRow hover onClick={() => setOpen((v) => !v)} sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Свернуть' : 'Подробнее'}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.name}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtInt(all.orders)}</TableCell>
        {ROLE_DEFS.map((role) => (
          <TableCell key={role.key} align="right">
            {Number(row[role.key]?.orders) > 0 ? fmtInt(row[role.key].orders) : <Muted>—</Muted>}
          </TableCell>
        ))}
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtHours(all.avg_hours)}</TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtHours(all.median_hours)}</TableCell>
        <TableCell><ZoneBar zones={all.zones} thresholds={thresholds} /></TableCell>
        {withPlan && <TableCell><PlanBar plan={all.plan} /></TableCell>}
      </TableRow>
      <TableRow>
        <TableCell colSpan={withPlan ? 10 : 9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5, py: 1.5 }}>
              {ROLE_DEFS.map((role) => {
                const b = row[role.key] || {};
                return (
                  <Box key={role.key} sx={{ p: 1.25, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="labelMedium" color="text.secondary">{role.label}</Typography>
                    {Number(b.orders) > 0 ? (
                      <>
                        <Typography variant="bodyMedium" sx={{ mt: 0.25 }}>
                          {fmtInt(b.orders)} зак. · ср. {fmtHours(b.avg_hours)} · мед. {fmtHours(b.median_hours)}
                        </Typography>
                        <Box sx={{ mt: 0.75 }}><ZoneBar zones={b.zones} thresholds={thresholds} width={160} /></Box>
                        {withPlan && planRated(b.plan) > 0 && <Box sx={{ mt: 0.75 }}><PlanBar plan={b.plan} width={160} /></Box>}
                      </>
                    ) : (
                      <Typography variant="bodySmall" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>Не участвовал</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function EmployeesBlock({ employees, thresholds, groups }) {
  const self = employees?.scope === 'self';
  const rows = list(employees?.rows).slice().sort((a, b) => (Number(b.all?.orders) || 0) - (Number(a.all?.orders) || 0));
  const ranges = zoneRanges(thresholds);
  const withPlan = groups.plan.length > 0 && rows.some((r) => planRated(r.all?.plan) > 0);
  const hoursNote = groups.plan.length && groups.hours.length ? ` (${groups.hoursLabel})` : '';
  const head = [
    { label: '' },
    { label: 'Сотрудник' },
    { label: 'Заказов', align: 'right' },
    ...ROLE_DEFS.map((r) => ({ label: r.label, align: 'right' })),
    { label: `Среднее${hoursNote}`, align: 'right' },
    { label: `Медиана${hoursNote}`, align: 'right' },
    { label: `Зоны (${ranges.good} / ${ranges.mid} / ${ranges.bad})${hoursNote}` },
    ...(withPlan ? [{ label: `${groups.plan.map(mpLabel).join(' + ')}: плановая дата` }] : []),
  ];
  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle
        title={self ? 'Мои показатели' : 'Сотрудники'}
        caption={self
          ? 'видны только ваши показатели'
          : 'часы от создания заказа до отгрузки по заказам с участием сотрудника; строку можно раскрыть по ролям'}
      />
      <Panel>
        {rows.length ? (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {head.map((col, idx) => (
                    <TableCell key={idx} align={col.align} sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: 'text.secondary' }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => <EmployeeRow key={row.user_id ?? row.name} row={row} thresholds={thresholds} withPlan={withPlan} />)}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyNote text="Нет данных за период" />
        )}
        {employees && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{ display: 'block', px: 2, py: 1.25, borderTop: (t) => `1px solid ${t.palette.outlineVariant}` }}
          >
            Отгружено заказов за период: {fmtInt(employees.shipped_orders)}, из них без сотрудника (без подбора): {fmtInt(employees.without_employee)}
          </Typography>
        )}
      </Panel>
    </Box>
  );
}

const PROBLEM_KEYS = ['ms_demand_errors', 'loading_errors', 'composition_alerts', 'print_failed'];

const PROBLEM_DAY_COLUMNS = [
  { key: 'date', label: 'Дата', render: (r) => shortDate(r.date), nowrap: true },
  { key: 'ms_demand_errors', label: 'Ошибки МС', align: 'right', render: (r) => fmtInt(r.ms_demand_errors) },
  { key: 'loading_errors', label: 'Ошибки погрузки', align: 'right', render: (r) => fmtInt(r.loading_errors) },
  { key: 'composition_alerts', label: 'Расхождения состава', align: 'right', render: (r) => fmtInt(r.composition_alerts) },
  { key: 'print_failed', label: 'Сбои печати', align: 'right', render: (r) => fmtInt(r.print_failed) },
];

const PRINT_FAILED_COLUMNS = [
  { key: 'at', label: 'Когда', render: (r) => fmtDt(r.at), nowrap: true },
  { key: 'type', label: 'Документ', render: (r) => printDocLabel(r.document_type), nowrap: true },
  { key: 'mp', label: 'МП', render: (r) => <MpBadge mp={r.marketplace} /> },
  { key: 'stage', label: 'Этап сбоя', render: (r) => r.failure_stage || '—', nowrap: true },
  { key: 'error', label: 'Ошибка', minWidth: 220, maxWidth: 480, render: (r) => <ErrorText text={r.error} /> },
];

function ProblemsPeriod({ problems }) {
  const byDay = list(problems?.by_day);
  const activeDays = byDay.filter((d) => PROBLEM_KEYS.some((k) => Number(d[k]) > 0));
  const totals = {};
  for (const k of PROBLEM_KEYS) totals[k] = byDay.reduce((sum, d) => sum + (Number(d[k]) || 0), 0);
  const byType = list(problems?.print_failed_by_type).slice().sort((a, b) => (Number(b.count) || 0) - (Number(a.count) || 0));
  const printFailed = list(problems?.print_failed);
  const loadingErrors = list(problems?.loading_errors);

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle title="Проблемы за период" />
      <Grid container spacing={2} sx={{ mb: 1.5 }}>
        <Grid item xs={12} lg={6}>
          <Panel sx={{ height: '100%' }}>
            <PanelHeader title="По дням" caption={activeDays.length ? 'только дни с проблемами' : null} />
            {activeDays.length ? (
              <SimpleTable
                columns={PROBLEM_DAY_COLUMNS}
                rows={activeDays}
                rowKey={(r) => r.date}
                maxHeight={360}
                footer={activeDays.length > 1 ? <TotalRow columns={PROBLEM_DAY_COLUMNS} row={totals} /> : null}
              />
            ) : (
              <EmptyNote text="Всё в порядке — проблем за период не было" />
            )}
          </Panel>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Panel sx={{ height: '100%' }}>
            <PanelHeader title="Сбои печати по документам" />
            {byType.length ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2 }}>
                {byType.map((row) => (
                  <Chip
                    key={row.document_type}
                    variant="outlined"
                    color="error"
                    label={`${printDocLabel(row.document_type)} — ${fmtInt(row.count)}`}
                  />
                ))}
              </Box>
            ) : (
              <EmptyNote text="Сбоев печати не было" />
            )}
          </Panel>
        </Grid>
      </Grid>
      <CountAccordion title="Сбои печати" count={printFailed.length}>
        <SimpleTable columns={PRINT_FAILED_COLUMNS} rows={printFailed} rowKey={(r) => r.id} maxHeight={420} empty="Всё в порядке" />
      </CountAccordion>
      <CountAccordion title="Ошибки погрузки" count={loadingErrors.length}>
        <SimpleTable columns={LOADING_ERROR_COLUMNS} rows={loadingErrors} maxHeight={420} empty="Всё в порядке" />
      </CountAccordion>
    </Box>
  );
}

function AnalyticsTab({ data, loading, error, from, to, onRange, thresholds, isMobile }) {
  const days = list(data?.days);
  const t = thresholdsOf(thresholds, data?.thresholds);
  const sources = shippedSourcesText(data?.shipped_sources);
  const groups = mpGroups(data?.by_marketplace, data?.plan_day_marketplaces);
  return (
    <Box>
      <PeriodToolbar from={from} to={to} onRange={onRange} isMobile={isMobile} />
      <Box sx={{ height: 4, mb: 1.5 }}>{loading && <LinearProgress />}</Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!data && loading && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 6 }, (_, i) => (
            <Grid item xs={6} md={4} lg={2} key={i}><Skeleton variant="rounded" height={148} /></Grid>
          ))}
        </Grid>
      )}

      {data && (
        <>
          <KpiRow total={data.total} byMarketplace={data.by_marketplace} planMps={data.plan_day_marketplaces} thresholds={t} />

          <SectionTitle title="Динамика по дням" caption={formatRange(data.period?.from || from, data.period?.to || to)} />
          {days.length
            ? <AnalyticsCharts days={days} thresholds={t} groups={groups} />
            : <Panel sx={{ mb: 3 }}><EmptyNote text="Нет данных за период" /></Panel>}

          <ByMarketplaceTable rows={data.by_marketplace} total={data.total} thresholds={t} groups={groups} />
          <ByDayTable days={days} total={data.total} thresholds={t} groups={groups} />
          {sources && (
            <Typography variant="bodySmall" color="text.secondary" sx={{ display: 'block', mb: 3, px: 0.5 }}>
              Источник отметки отгрузки: {sources}.
            </Typography>
          )}

          <EmployeesBlock employees={data.employees} thresholds={t} groups={groups} />
          <ProblemsPeriod problems={data.problems} />
        </>
      )}
    </Box>
  );
}

// ============================================================
// Страница
// ============================================================

export default function FulfillmentDashboard() {
  const isMobile = useIsMobile();
  const location = useLocation();

  // Состояние в URL пишем через history.replaceState, а не через роутер:
  // навигация перемонтирует страницу (ProtectedRoute объявлен внутри App).
  const [query, setQuery] = useState(() => resolveQuery(parseQuery(location.search)));
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');

  const [now, setNow] = useState(null);
  const [nowLoading, setNowLoading] = useState(false);
  const [nowError, setNowError] = useState('');
  const nowCtl = useRef(null);

  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');

  const { tab, from, to } = query;
  const paramsKey = JSON.stringify(filterParams(query));
  const params = useMemo(() => JSON.parse(paramsKey), [paramsKey]);
  const thresholds = thresholdsOf(meta?.thresholds, now?.thresholds);
  const ranges = zoneRanges(thresholds);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const search = buildQuery(query);
    window.history.replaceState(window.history.state, '', `${location.pathname}${search ? `?${search}` : ''}`);
  }, [query, location.pathname]);

  useEffect(() => {
    const controller = new AbortController();
    axios.get('/fulfillment-dashboard/meta', { silent: true, signal: controller.signal })
      .then((res) => {
        const data = res.data || null;
        setMeta(data);
        setMetaError('');
        if (data) setQuery((prev) => resolveQuery(prev, { meta: data }));
      })
      .catch((err) => {
        if (!isAbort(err)) setMetaError(errMessage(err));
      });
    return () => controller.abort();
  }, []);

  // force — смена фильтров: прерываем текущий запрос; таймер и кнопка не стартуют параллельный.
  const loadNow = useCallback((force) => {
    if (nowCtl.current) {
      if (!force) return;
      nowCtl.current.abort();
    }
    const controller = new AbortController();
    nowCtl.current = controller;
    setNowLoading(true);
    axios.get('/fulfillment-dashboard/now', { params, silent: true, signal: controller.signal })
      .then((res) => {
        setNow(res.data || null);
        setNowError('');
      })
      .catch((err) => {
        if (!isAbort(err)) setNowError(errMessage(err));
      })
      .finally(() => {
        if (nowCtl.current !== controller) return;
        nowCtl.current = null;
        setNowLoading(false);
      });
  }, [params]);

  useEffect(() => {
    if (tab !== 'now') return undefined;
    loadNow(true);
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadNow(false);
    }, REFRESH_MS);
    return () => {
      clearInterval(timer);
      if (nowCtl.current) nowCtl.current.abort();
      nowCtl.current = null;
      setNowLoading(false);
    };
  }, [tab, loadNow]);

  useEffect(() => {
    if (tab !== 'analytics') return undefined;
    const controller = new AbortController();
    setAnalyticsLoading(true);
    setAnalyticsError('');
    axios.get('/fulfillment-dashboard/analytics', {
      params: { ...params, from, to },
      silent: true,
      signal: controller.signal,
    })
      .then((res) => {
        setAnalytics(res.data || null);
        setAnalyticsLoading(false);
      })
      .catch((err) => {
        if (isAbort(err)) return;
        setAnalytics(null);
        setAnalyticsError(errMessage(err));
        setAnalyticsLoading(false);
      });
    return () => controller.abort();
  }, [tab, from, to, params]);

  const onFilterChange = (change) => {
    setQuery((prev) => resolveQuery(applyFilterChange(prev, change, meta), { meta }));
  };

  const selfOnly = meta?.can_view_staff === false || analytics?.employees?.scope === 'self';

  return (
    <Box>
      <PageHeader
        title="Сборка и отгрузка"
        subtitle="FBS-заказы WB, Ozon и Яндекс Маркета: очередь, сборка, отгрузка"
        actions={selfOnly ? <Chip size="small" variant="outlined" label="Показатели сотрудников — только ваши" /> : null}
      />

      <Tabs
        value={tab}
        onChange={(_, value) => setQuery((prev) => ({ ...prev, tab: value }))}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: (t) => `1px solid ${t.palette.outlineVariant}` }}
      >
        <Tab value="now" label="Сейчас" />
        <Tab value="analytics" label="Аналитика" />
      </Tabs>

      {metaError && <Alert severity="warning" sx={{ mb: 2 }}>Справочники не загрузились: {metaError}</Alert>}

      <FiltersBar meta={meta} query={query} onChange={onFilterChange} isMobile={isMobile} />

      {tab === 'now' && (
        <NowTab
          now={now}
          loading={nowLoading}
          error={nowError}
          onRefresh={() => loadNow(false)}
          thresholds={thresholds}
        />
      )}

      {tab === 'analytics' && (
        <AnalyticsTab
          data={analytics}
          loading={analyticsLoading}
          error={analyticsError}
          from={from}
          to={to}
          onRange={(nextFrom, nextTo) => setQuery((prev) => resolveQuery({ ...prev, from: nextFrom, to: nextTo }, { meta }))}
          thresholds={thresholds}
          isMobile={isMobile}
        />
      )}

      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ display: 'block', mt: 4, pt: 2, borderTop: (t) => `1px solid ${t.palette.outlineVariant}`, lineHeight: 1.6 }}
      >
        Время считается в календарных часах от создания заказа на маркетплейсе. WB и Ozon оцениваются по зонам:
        {' '}{ranges.good} — норма, {ranges.mid} — средне, {ranges.bad} — плохо. Яндекс Маркет — по отгрузке в плановую
        дату: в плановый день — хорошо, раньше или позже — плохо. Отгрузка WB — раньшее из закрытия нашей погрузки
        и скана ШК поставки WB, Яндекса — раньшее из закрытия нашей погрузки и закрытия отгрузки в кабинете Яндекса.
        Дни периода — по московскому времени.
      </Typography>
    </Box>
  );
}
